/**
 * Analytics utilities — optimized to avoid forced reflows and layout thrashing
 */

// --- Utility Helpers ---

// Debounce function to prevent excessive calls
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Throttle function to limit function calls
export function throttle(func, limit) {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// --- Safe DOM Queries ---

/**
 * Prevents forced reflows by querying DOM in a read-optimized phase (rAF)
 */
export function safeQuerySelector(selector) {
  return new Promise((resolve) => {
    const findElement = () => requestAnimationFrame(() => resolve(document.querySelector(selector)));

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', findElement);
    } else {
      findElement();
    }
  });
}

/**
 * Batches layout reads together to avoid reflows
 */
let pendingReads = [];
let scheduledRead = false;

export function safeGetBoundingClientRect(element) {
  return new Promise((resolve) => {
    pendingReads.push({ element, resolve });

    if (!scheduledRead) {
      scheduledRead = true;
      requestAnimationFrame(() => {
        for (const { element, resolve } of pendingReads) {
          resolve(element.getBoundingClientRect());
        }
        pendingReads = [];
        scheduledRead = false;
      });
    }
  });
}

// --- Analytics & Clarity Optimizations ---

/**
 * Loads analytics scripts (e.g. Google Analytics, Clarity) during browser idle time
 */
export function loadAnalyticsWithIdleCallback(loadFunction, timeout = 2000) {
  if (typeof window === 'undefined') return;

  const loadAnalytics = () => {
    try {
      loadFunction();
    } catch (error) {
      console.warn('Analytics loading failed:', error);
    }
  };

  // Use requestIdleCallback if available, otherwise fallback to timeout
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(loadAnalytics, { timeout });
  } else {
    setTimeout(loadAnalytics, timeout);
  }
}

// Microsoft Clarity optimization
// NOTE: Clarity optimization is now handled in public/index.html to prevent forced reflows
// This function is kept for backwards compatibility but does nothing to avoid conflicts
export function optimizeClarityLoading() {
  if (typeof window === 'undefined') return;

  // Clarity optimization is handled in index.html before any scripts load
  // This prevents conflicts and ensures the optimization runs early
}

/**
 * Observes layout shifts (CLS) for debugging
 */
export function initializeAnalyticsOptimizations() {
  if (typeof window === 'undefined') return;

  // Clarity optimization is handled in index.html to run before any scripts load
  // No need to call optimizeClarityLoading() here to avoid conflicts

  if ('PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'layout-shift' && !entry.hadRecentInput && entry.value > 0.1) {
            console.warn('⚠️ Layout shift detected:', entry);
          }
        }
      });
      observer.observe({ type: 'layout-shift', buffered: true });
    } catch (err) {
      // Some browsers might not support layout-shift
    }
  }
}

// --- Event Handlers ---

export const debouncedResizeHandler = debounce(() => {
  if (window.gtag) {
    window.gtag('event', 'resize', {
      event_category: 'performance',
      event_label: 'window_resize',
    });
  }
}, 250);

export const throttledScrollHandler = throttle(() => {
  if (window.gtag) {
    window.gtag('event', 'scroll', {
      event_category: 'engagement',
      event_label: 'page_scroll',
    });
  }
}, 1000);

// --- Default Export ---

export default {
  debounce,
  throttle,
  safeQuerySelector,
  safeGetBoundingClientRect,
  loadAnalyticsWithIdleCallback,
  optimizeClarityLoading,
  initializeAnalyticsOptimizations,
  debouncedResizeHandler,
  throttledScrollHandler,
};
